import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, History, LayoutDashboard, Settings, Share2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { fetchWithRetry } from "../utils/api";
import { providerPersistsHistory } from "../utils/providerPolicy";

const LeagueContextActions = ({ league, provider, leagueYear, leagueId, activePage }) => {
    const baseUrl = `/fantasy_stats/league/${provider}/${leagueYear}/${leagueId}`;
    const supportsTradeHub = league?.capabilities?.supports_player_stats !== false;
    const supportsHistory =
        league?.capabilities?.supports_history_store !== false && providerPersistsHistory(provider);
    const actions = [
        { key: "hub", label: "League Hub", to: `${baseUrl}/`, icon: LayoutDashboard },
        { key: "settings", label: "Settings", to: `${baseUrl}/settings/`, icon: Settings },
        { key: "history", label: "History", to: `/fantasy_stats/league-history/${provider}/${leagueYear}/${leagueId}/`, icon: History, enabled: supportsHistory },
        { key: "cup", label: "League Cup", to: `/fantasy_stats/league-cup/${provider}/${leagueYear}/${leagueId}/`, icon: Trophy },
        { key: "tradehub", label: "Trade Hub", to: `/fantasy_stats/trade-hub/${provider}/${leagueYear}/${leagueId}/`, icon: ArrowLeftRight, enabled: supportsTradeHub },
    ].filter(({ key, enabled = true }) => enabled && key !== activePage);

    const handleShare = async () => {
        if (!league?.id) return;
        try {
            const response = await fetchWithRetry(`/api/league/${league.id}/share/`, { method: "POST" }, 1);
            const data = await response.json();
            if (!response.ok) {
                toast.error(data.error || "Failed to generate share link.");
                return;
            }
            // The API may return a path-only URL (empty FRONTEND_URL); resolve it
            // against the current site so the copied link is shareable.
            const shareUrl = new URL(data.url, window.location.origin).href;
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Share link copied to clipboard!");
            } catch {
                window.prompt("Copy share link:", shareUrl);
            }
        } catch {
            toast.error("An unexpected error occurred.");
        }
    };

    return (
        <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
            <button
                type="button"
                onClick={handleShare}
                disabled={!league?.id}
                className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded border border-white/20 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 transition-colors hover:border-white/40 hover:text-white disabled:cursor-wait disabled:opacity-60"
                title="Copy share link"
            >
                <Share2 className="h-3.5 w-3.5" />
                Share
            </button>
            {actions.map(({ key, label, to, icon: Icon }) => (
                <Link
                    key={key}
                    to={to}
                    className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded border border-white/20 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 transition-colors hover:border-white/40 hover:text-white"
                >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                </Link>
            ))}
        </div>
    );
};

export default LeagueContextActions;
